import type {
  ActionCategory,
  AiAction,
  CatalogueModel,
  CostTier,
  ProviderKey,
  ProviderStatus,
} from '../../api/ai-settings';

export const TIER_LABEL: Record<CostTier, string> = {
  low: 'Low cost',
  medium: 'Medium cost',
  high: 'High cost',
};

export const TIER_VARIANT: Record<CostTier, 'success' | 'info' | 'warning'> = {
  low: 'success',
  medium: 'info',
  high: 'warning',
};

export const CATEGORY_LABEL: Record<ActionCategory, string> = {
  conversation: 'Conversation',
  writing: 'Writing',
  classification: 'Classification',
  extraction: 'Extraction',
  vision: 'Vision',
};

export const CATEGORY_HINT: Record<ActionCategory, string> = {
  conversation: 'Answers people in chat',
  writing: 'Writes a sentence or document',
  classification: 'Picks a label; needs tool calling',
  extraction: 'Returns structured data; needs tool calling',
  vision: 'Reads images; needs tool calling and image input',
};

/** USD per million tokens: "$0.10", "$12", "$0.075". */
export function usdPerM(n: number): string {
  if (n === 0) return '$0';
  if (n >= 10) return `$${Math.round(n * 100) / 100}`;
  if (n >= 1) return `$${n.toFixed(2).replace(/\.?0+$/, '')}`;
  return `$${n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}`;
}

/** Whole-dollar-ish spend: "$0.21", "$45", "$1,240". */
export function usdSpend(n: number): string {
  if (n >= 100) return `$${Math.round(n).toLocaleString('en-US')}`;
  return `$${n.toFixed(2)}`;
}

/**
 * The catalogue models an action may be given on a provider: switched on, that
 * vendor's, and able to do what the action needs — image input for a vision
 * action, tool calling for a classifier. The backend enforces the same rules;
 * this only keeps the picker from offering a choice it will refuse.
 */
export function eligibleModels(
  catalogue: CatalogueModel[],
  action: Pick<AiAction, 'needs'>,
  provider: ProviderKey,
  /** A model already assigned stays listed even if since switched off, so the
   *  select can show what is actually set. */
  keepId?: string | null,
): CatalogueModel[] {
  return catalogue
    .filter((m) => m.provider === provider)
    .filter(
      (m) =>
        m.modelId === keepId ||
        (m.active &&
          (!action.needs.tools || m.supportsTools) &&
          (!action.needs.vision || m.supportsVision)),
    )
    .sort((a, b) => a.blendedUsdPerMTok - b.blendedUsdPerMTok);
}

/** Why a vendor's column cannot serve an action right now, or null. */
export function unavailableReason(
  action: Pick<AiAction, 'containsNames' | 'needs'>,
  provider: ProviderStatus | undefined,
): string | null {
  if (!provider) return null;
  if (action.containsNames && provider.key !== 'anthropic') {
    return 'Sends names: Anthropic only';
  }
  if (action.needs.vision && !provider.visionEnabled) {
    return 'Images not allowed for this vendor (deploy setting)';
  }
  return null;
}

export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** Same ordered content: used to know when routing has unsaved changes. */
export function sameRouting(
  a: { defaultProvider: string; fallbackOrder: string[] },
  b: { defaultProvider: string; fallbackOrder: string[] },
): boolean {
  return (
    a.defaultProvider === b.defaultProvider &&
    a.fallbackOrder.length === b.fallbackOrder.length &&
    a.fallbackOrder.every((p, i) => p === b.fallbackOrder[i])
  );
}
